import * as React from 'react';
import { Field, touch } from 'redux-form';
import ReduxFormInputField from './ReduxFormInputField';
import CheckBoxUI from './CheckBoxUI';
import { Checkbox } from 'redux-form-material-ui';
import FooterUI from './FooterUI';
import { ILeadFormConstants } from './LeadFormConstants';
import { CardHeaderUI } from '@makemydeal/dr-activities-common';
import { normalizePhone } from '../../utils';

type BlurReportableField = 'firstName' | 'lastName' | 'phone' | 'email' | 'message';
type ReduxLeadFormState = {
    blurReport: Record<BlurReportableField, boolean>;
};

class ReduxLeadForm extends React.Component<any, ReduxLeadFormState> {
    constructor(props: any) {
        super(props);
        this.state = {
            blurReport: {
                firstName: true,
                lastName: true,
                phone: true,
                email: true,
                message: true
            }
        };
    }

    componentDidMount() {
        this.props.uspsZipInitialize();
    }

    touchFields(field: string) {
        // TODO: accepts array of strings
        this.props.dispatch(touch('leadForm', `shopper.${field}`));
        this.props.onCommunicationPreferenceChange();
    }

    getMessageText(message: string): string {
        const { maxMessageLength } = this.props;
        // if we have a message and its length is one less than the max use 'character'
        const messageText = message && message.length === maxMessageLength - 1 ? 'character' : 'characters';

        return `${messageText} remaining`;
    }

    renderHeadingSection = (leadFormConstants: ILeadFormConstants) => {
        if (!leadFormConstants.heading) return null;
        return (
            <>
                <div className="summary-lead-form-information">{leadFormConstants.heading}</div>
                <div className="summary-lead-form-disclaimer">{leadFormConstants.subHeading}</div>
            </>
        );
    };

    renderHeader = () => {
        const { isLeadFormLite, hideTitle } = this.props;
        if (!hideTitle) {
            if (isLeadFormLite) {
                return (
                    <p className="lead-form-information" id="sp_lead_form_info">
                        What's the best way to reach you?
                    </p>
                );
            } else {
                return (
                    <div>
                        <h3 className="lead-form-title" id="sp_lead_form_title">
                            Send My Deal Summary
                        </h3>
                        <p className="lead-form-information" id="sp_lead_form_info">
                            Save time on your purchase
                        </p>
                        <p className="dr-sp-disclaimer lead-form-disclaimer" id="sp_lead_form_disclaimer">
                            Send your progress and receive a copy of your deal by email.
                        </p>
                    </div>
                );
            }
        }
        return null;
    };

    renderMessageBox = () => {
        const { hideAdditionalInfo, messageNormalizer, maxMessageLength, messageValue } = this.props;
        const messageLength = messageValue ? maxMessageLength - messageValue.length : maxMessageLength;
        if (hideAdditionalInfo) {
            return null;
        }

        return (
            <div className="form-group">
                <p className="message-question" id="sp_lead_form_msg_question">
                    Do you have any additional information?
                </p>
                <Field
                    id="sp_shopper_message"
                    className="form-control"
                    name="message"
                    component="textarea"
                    placeholder="Ex: I'd like to buy a car in the next week. (Optional)"
                    data-analytics="input-deal-message"
                    onBlur={() => this.reportBlurEvent('message')}
                    normalize={messageNormalizer}
                    rows="2"
                    aria-label="Additional Information"
                />
                <div id="sp_lead_form_msg_counter" className="lead-msg-count">
                    {messageLength} {this.getMessageText(messageValue)}
                </div>
            </div>
        );
    };

    onContinueToNextStepClick = () => {
        const { onContinueToNextStepClick } = this.props;
        onContinueToNextStepClick();
    };

    renderButtonFooter = () => {
        const { offerSubmitting, offerHasBeenSent, hideContinueButton, hideNextStepsLink, zipCodeLoading } = this.props;

        if (hideContinueButton) {
            return null;
        }
        if (this.props.isLeadFormLite) {
            return (
                <FooterUI
                    offerHasBeenSent={offerHasBeenSent}
                    onActionButtonClick={() => null}
                    onContinueLinkClick={hideNextStepsLink ? undefined : this.onContinueToNextStepClick}
                    processing={offerSubmitting || zipCodeLoading}
                />
            );
        } else {
            return (
                <FooterUI
                    offerHasBeenSent={this.props.offerHasBeenSent}
                    onActionButtonClick={() => null}
                    processing={this.props.offerSubmitting || zipCodeLoading}
                />
            );
        }
    };

    checkForNonEmptyValue = (e: any): boolean => {
        const { initialValues, leadFormFieldState } = this.props;
        const formFieldName = e.target.name.split('.')[1];
        const validNonEmptyValueWasEntered = e.target.value !== '' && e.target.value !== initialValues.shopper[formFieldName];
        if (validNonEmptyValueWasEntered) {
            leadFormFieldState[formFieldName].hasBeenUpdated = true;
            leadFormFieldState[formFieldName].previousValue = e.target.value;
            return true;
        } else {
            return false;
        }
    };

    reportBlurEvent = (field: BlurReportableField) => {
        const { blurReport } = this.state;
        const { onFirstNameBlur, onLastNameBlur, onPhoneBlur, onEmailBlur, onMessageBlur } = this.props;

        switch (field) {
            case 'firstName':
                onFirstNameBlur(blurReport.firstName);
                break;
            case 'lastName':
                onLastNameBlur(blurReport.lastName);
                break;
            case 'phone':
                onPhoneBlur(blurReport.phone);
                break;
            case 'email':
                onEmailBlur(blurReport.email);
                break;
            case 'message':
                onMessageBlur(blurReport.message);
                break;
        }

        this.setState({ blurReport: { ...blurReport, [field]: false } });
    };

    render() {
        const {
            error,
            constructErrorMessage,
            handleSubmit,
            offerSubmitFailed,
            submitFailed,
            shopperConsentText,
            currentShopperInfo,
            isTcpaEnabled,
            isTCPACheckboxHidden,
            CCPAConsentText,
            hidePhoneNumber,
            hideContactPreffered,
            hideUserConsent,
            leadFormCCPAShown,
            hideMessageBox,
            isLeadFormShort,
            hideEmailPhoneFields,
            hideNameFields,
            leadFormConstants,
            isSingleColumn,
            isLeadFormSingleCol,
            isPhoneNumberRequired
        } = this.props;

        const shopperInfo = currentShopperInfo && currentShopperInfo.contactPreferences;
        const hideContactPreferencesError = shopperInfo ? shopperInfo.email || shopperInfo.phone || shopperInfo.sms : false;
        const errorMessage = constructErrorMessage(error, submitFailed, offerSubmitFailed);
        const errorMsgComponent = (
            <div className={isLeadFormShort ? 'form-group' : 'form-group text-right'}>
                <span className="message-error"> {errorMessage} </span>
            </div>
        );
        const errorClsContactsPreference = 'preferred-method-communication'.concat(
            !hideContactPreferencesError ? ' error-ring' : ''
        );
        const leadformLayout = (fullWidthClass: string, containerClass: string) => {
            let leadFormClass;
            if (isLeadFormSingleCol) {
                leadFormClass = 'form-group col';
            } else if (isSingleColumn) {
                leadFormClass = fullWidthClass;
            } else {
                leadFormClass = containerClass;
            }
            return leadFormClass;
        };

        const showOptionalLabel = isPhoneNumberRequired || shopperInfo?.phone || shopperInfo?.sms ? '' : '(Optional)';
        return (
            <div
                style={{ width: '100%' }}
                className={isLeadFormShort ? 'dr-short-lead dr-leadform-wrapper' : 'dr-leadform-wrapper'}
            >
                <form onSubmit={handleSubmit} className="lead-form">
                    <CardHeaderUI cardHeading={leadFormConstants.pageTitle} />
                    {this.renderHeader()}
                    {hideNameFields ? null : (
                        <>
                            <div className={leadformLayout('full-width-name-container', 'full-width-name-container')}>
                                <div className="form-group">
                                    <Field
                                        id="sp_shopper_firstname"
                                        ref="spLfFirstName"
                                        withRef={true}
                                        name="shopper.firstName"
                                        component={ReduxFormInputField}
                                        analyticsId="input-shopper-first-name"
                                        type="text"
                                        onBlur={(e) => this.checkForNonEmptyValue(e) && this.reportBlurEvent('firstName')}
                                        label="First Name"
                                    />
                                </div>
                            </div>
                            <div className={leadformLayout('full-width-name-container', 'full-width-name-container')}>
                                <div className="form-group">
                                    <Field
                                        id="sp_shopper_lastname"
                                        ref="spLfLastName"
                                        withRef={true}
                                        name="shopper.lastName"
                                        component={ReduxFormInputField}
                                        analyticsId="input-shopper-last-name"
                                        type="text"
                                        onBlur={(event) => this.checkForNonEmptyValue(event) && this.reportBlurEvent('lastName')}
                                        label="Last Name"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    {hideEmailPhoneFields ? null : (
                        <div className={leadformLayout('full-width-email-phone-container ', 'email-phone-container')}>
                            <div className="form-group col">
                                <Field
                                    id="sp_shopper_email"
                                    ref="spLfEmail"
                                    withRef={true}
                                    name="shopper.email"
                                    component={ReduxFormInputField}
                                    analyticsId="input-shopper-email"
                                    type="text"
                                    onBlur={(event) => this.checkForNonEmptyValue(event) && this.reportBlurEvent('email')}
                                    label="Email"
                                />
                            </div>

                            {hidePhoneNumber ? null : (
                                <div className="form-group col">
                                    <Field
                                        id="sp_shopper_phone"
                                        ref="spLfPhone"
                                        withRef={true}
                                        name="shopper.phone"
                                        component={ReduxFormInputField}
                                        analyticsId="input-shopper-phone"
                                        normalize={normalizePhone}
                                        type="tel"
                                        onBlur={(event) => this.checkForNonEmptyValue(event) && this.reportBlurEvent('phone')}
                                        label={`Phone${showOptionalLabel}`}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {hideContactPreffered ? null : (
                        <div className="communication-preference-container">
                            <h5 className="title">Please select your preferred method of contact.</h5>
                            <p className="information">You may still receive correspondence in other ways.</p>
                            <div className={errorClsContactsPreference}>
                                <Field
                                    id="sp_shopper_email_preference"
                                    name="shopper.contactPreferences.email"
                                    component={Checkbox}
                                    label="Email"
                                    onCheck={() => this.touchFields('email')}
                                    iconStyle={{ stroke: '#fff' }}
                                    labelStyle={{ fontWeight: shopperInfo && shopperInfo.email ? 'bold' : 'normal' }}
                                />
                                <Field
                                    id="sp_shopper_call_preference"
                                    name="shopper.contactPreferences.phone"
                                    component={Checkbox}
                                    label="Call"
                                    onCheck={() => this.touchFields('phone')}
                                    iconStyle={{ stroke: '#fff' }}
                                    labelStyle={{ fontWeight: shopperInfo && shopperInfo.phone ? 'bold' : 'normal' }}
                                />
                                <Field
                                    id="sp_shopper_text_preference"
                                    name="shopper.contactPreferences.sms"
                                    component={Checkbox}
                                    label="Text"
                                    onCheck={() => this.touchFields('phone')}
                                    iconStyle={{ stroke: '#fff' }}
                                    labelStyle={{ fontWeight: shopperInfo && shopperInfo.sms ? 'bold' : 'normal' }}
                                />
                            </div>
                        </div>
                    )}

                    {hideMessageBox ? null : this.renderMessageBox()}
                    {!hideUserConsent && isTcpaEnabled && (
                        <div className="form-check user-consent-group">
                            <Field
                                id="sp_shopper_consent"
                                name="shopper.userConsent"
                                component={CheckBoxUI}
                                normalize={(value) => (value ? 'checked' : 'unchecked')}
                                labelText={shopperConsentText}
                                isHidden={isTCPACheckboxHidden}
                            ></Field>
                        </div>
                    )}
                    {errorMessage ? errorMsgComponent : null}
                    {leadFormCCPAShown && <div data-testid="shopper.ccpaConsent">{CCPAConsentText}</div>}
                    <div className="leadform__footer">{this.renderButtonFooter()}</div>
                </form>
            </div>
        );
    }
}

export default ReduxLeadForm;
